import {
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { t } from "@/lib/i18n/interpolate";
import { decompose, type Salt } from "@/lib/tool-kit/chem-reference/ions";
import {
  compareQ,
  findBySalt,
  ionProduct,
} from "@/lib/tool-kit/chem-reference/ksp";
import {
  afterMixing,
  type Equations,
  equations,
  type Mixture,
  mix,
  type Product,
  writeEquation,
} from "@/lib/tool-kit/chem-reference/precipitation";
import type { Status } from "@/lib/tool-kit/chem-reference/solubility-rules";
import {
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";
import { parseFormula } from "@/lib/tool-kit/chemistry/formula";

export interface MixStrings {
  statusNames: Record<Status, string>;
  emptyPrompt: string;
  formulaError: string;
  notSalt: string;
  sameCation: string;
  sameAnion: string;
  precipitateYes: string;
  precipitateNo: string;
  precipitates: string;
  productsHeading: string;
  productLine: string;
  slightlyNote: string;
  molecularLabel: string;
  completeLabel: string;
  netLabel: string;
  noNet: string;
  spectatorsLabel: string;
  qHeading: string;
  qLine: string;
  qOver: string;
  qUnder: string;
  qSaturated: string;
  qNoKsp: string;
  qNeedValues: string;
}

export interface MixInputs {
  first: string;
  second: string;
  c1: number | null;
  v1: number | null;
  c2: number | null;
  v2: number | null;
}

const SIG = 2;
const STATE_SUFFIX = /\s*\((?:aq|s|l|g)\)\s*$/;

function readSalt(text: string): Salt | "empty" | "formula" | "notSalt" {
  if (text.trim() === "") {
    return "empty";
  }
  if (!parseFormula(text.replace(STATE_SUFFIX, "")).ok) {
    return "formula";
  }
  return decompose(text) ?? "notSalt";
}

function QCheck({
  precipitate,
  mixture,
  inputs,
  strings,
  fmt,
}: {
  precipitate: Product;
  mixture: Mixture;
  inputs: MixInputs;
  strings: MixStrings;
  fmt: NumberFormatter;
}) {
  const { c1, v1, c2, v2 } = inputs;
  if (c1 === null || v1 === null || c2 === null || v2 === null) {
    return <Notice>{strings.qNeedValues}</Notice>;
  }
  const entry = findBySalt(precipitate.salt);
  if (!entry) {
    return (
      <Notice>{t(strings.qNoKsp, { formula: precipitate.formula })}</Notice>
    );
  }
  const { cation, anion } = afterMixing(precipitate, mixture, c1, v1, c2, v2);
  const { nCation, nAnion } = precipitate.salt;
  const q = ionProduct(cation, anion, nCation, nAnion);
  const outcome = compareQ(q, entry.ksp);
  const key = { over: "qOver", under: "qUnder", saturated: "qSaturated" }[
    outcome
  ] as "qOver" | "qUnder" | "qSaturated";
  return (
    <div className={WORKING_BOX}>
      <p>
        {t(strings.qLine, {
          formula: precipitate.formula,
          cation: formatSig(fmt, cation, SIG),
          anion: formatSig(fmt, anion, SIG),
          q: formatSig(fmt, q, SIG),
          ksp: formatSig(fmt, entry.ksp, SIG),
        })}
      </p>
      <p className="font-sans text-foreground">{strings[key]}</p>
    </div>
  );
}

function EquationBlock({
  eq,
  strings,
}: {
  eq: Equations;
  strings: MixStrings;
}) {
  return (
    <div className="space-y-2">
      <p className="text-foreground text-sm">
        <span className="font-medium">{strings.molecularLabel}</span>
      </p>
      <p className="break-words font-mono text-sm">
        {writeEquation(eq.molecular)}
      </p>
      <p className="text-foreground text-sm">
        <span className="font-medium">{strings.completeLabel}</span>
      </p>
      <p className="break-words font-mono text-sm">
        {writeEquation(eq.complete)}
      </p>
      <p className="text-foreground text-sm">
        <span className="font-medium">{strings.netLabel}</span>
      </p>
      <p className="break-words font-mono text-sm">
        {eq.net ? writeEquation(eq.net) : strings.noNet}
      </p>
    </div>
  );
}

export function MixResults({
  inputs,
  strings,
  fmt,
}: {
  inputs: MixInputs;
  strings: MixStrings;
  fmt: NumberFormatter;
}) {
  const first = readSalt(inputs.first);
  const second = readSalt(inputs.second);
  for (const salt of [first, second]) {
    if (salt === "empty") {
      return <Notice>{strings.emptyPrompt}</Notice>;
    }
    if (salt === "formula") {
      return <ErrorNotice>{strings.formulaError}</ErrorNotice>;
    }
    if (salt === "notSalt") {
      return <ErrorNotice>{strings.notSalt}</ErrorNotice>;
    }
  }
  const mixture = mix(first as Salt, second as Salt);
  if ("problem" in mixture) {
    return <ErrorNotice>{strings[mixture.problem]}</ErrorNotice>;
  }
  const eq = equations(mixture);
  const names = mixture.precipitates.map((p) => `${p.formula}(s)`).join(", ");
  const slightly = mixture.precipitates.some(
    (p) => p.verdict.status === "slightly"
  );
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.precipitates}
          value={
            mixture.precipitates.length > 0 ? names : strings.precipitateNo
          }
        />
      </StatGrid>
      <p className="text-foreground text-sm">
        {mixture.precipitates.length > 0
          ? t(strings.precipitateYes, { names })
          : strings.precipitateNo}
      </p>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.productsHeading}</h3>
        <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
          {mixture.products.map((p) => (
            <li key={p.formula}>
              {t(strings.productLine, {
                formula: p.formula,
                status: strings.statusNames[p.verdict.status],
              })}
            </li>
          ))}
        </ul>
        {slightly ? <Notice>{strings.slightlyNote}</Notice> : null}
      </div>
      <div className={WORKING_BOX}>
        <EquationBlock eq={eq} strings={strings} />
      </div>
      {eq.net ? (
        <p className="text-muted-foreground text-xs">
          {strings.spectatorsLabel}
        </p>
      ) : null}
      {mixture.precipitates.length > 0 ? (
        <div className="space-y-3">
          <h3 className={WIDGET_H3}>{strings.qHeading}</h3>
          {mixture.precipitates.map((p) => (
            <QCheck
              fmt={fmt}
              inputs={inputs}
              key={p.formula}
              mixture={mixture}
              precipitate={p}
              strings={strings}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}
